import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { isUnidentifiedLockedBySchedule } from '@/utils/coverage'
import type { ScheduleCoverageFlags } from '@/utils/coverage'

export type LockableSchedule = (ScheduleCoverageFlags & { id?: number | null }) | null | undefined

/** Slug da aba "Informações básicas" do EditScheduleModal (TAB_SLUGS). */
export const SCHEDULE_BASIC_INFO_TAB_SLUG = 'informacoes-basicas'

/**
 * Segmento "Ocupado sem identificação" desativado quando a escala não permite: estado,
 * permissão de abrir a configuração (`schedule_editing` na escala) e o deep-link para
 * Gerenciar escala com o EditScheduleModal aberto em Informações básicas.
 */
export function useUnidentifiedCoverageLock(schedule: () => LockableSchedule) {
  // `useRouter()` em vez de importar a instância: não puxa o grafo de rotas nos testes.
  const router = useRouter()
  const permissions = usePermissionsStore()

  const unidentifiedLocked = computed(() => isUnidentifiedLockedBySchedule(schedule()))
  const canOpenScheduleSettings = computed(() => {
    const id = schedule()?.id
    if (!id) return false
    return permissions.check({
      permission: AuthorizationPermissions.ScheduleEditing,
      resource: { objectType: AuthorizationResourcesAndSubjects.Schedule, objectId: id }
    })
  })

  /** `beforeNavigate` fecha o modal antes de sair, para ele não ficar sobreposto à outra tela. */
  function openScheduleSettings(beforeNavigate?: () => void) {
    const id = schedule()?.id
    if (!id) return
    beforeNavigate?.()
    router?.push({
      path: '/gerenciar-escala',
      query: { openScheduleId: String(id), editTab: SCHEDULE_BASIC_INFO_TAB_SLUG }
    })
  }

  return { unidentifiedLocked, canOpenScheduleSettings, openScheduleSettings }
}
